import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Dialog, DialogContent, DialogTitle, DialogActions, InfoPanel, Spinner, Typography } from '@otcmarketsgroup/otcm-ui-lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createCuf, getApplication } from '../../api/cuf';
import { warnings } from '../../constants';
import { useSyncApplication } from '../../hooks/useSyncApplication';
import { testIds } from '../../constants/testIds';

const StartForm = ({ symbol, email, triggerStart = 0 }) => {
    const [showDialog, setShowDialog] = useState(false);
    const lastHandledTriggerRef = useRef(0);
    const queryClient = useQueryClient();

    useEffect(() => {
        return () => {
            queryClient.removeQueries({ queryKey: ['application', symbol, email] });
        };
    }, [queryClient, symbol, email]);

    const handleCloseDialog = () => {
        if (mutation.isPending) return;
        setShowDialog(false);
    };

    const applicationQuery = useQuery({
        queryKey: ['application', symbol, email],
        queryFn: ({ signal }) => getApplication({ symbol, email }, signal),
        retry: false,
        enabled: false,
    });

    useSyncApplication(applicationQuery?.data, applicationQuery.isSuccess);

    useEffect(() => {
        if (applicationQuery.isSuccess) {
            setShowDialog(false);
        }
    }, [applicationQuery.isSuccess]);

    const mutation = useMutation({
        mutationFn: createCuf,
        onSuccess: () => {
            applicationQuery.refetch();
        },
        onError: (err) => {
            console.error(err);
        }
    });
    const { mutate } = mutation;

    const handleStart = () => {
        setShowDialog(true);
        mutate({ symbol, email });
    };

    useEffect(() => {
        if (triggerStart > 0 && triggerStart !== lastHandledTriggerRef.current) {
            lastHandledTriggerRef.current = triggerStart;
            setShowDialog(true);
            mutate({ symbol, email });
        }
    }, [triggerStart, symbol, email, mutate]);

    return (
        <>
            <Button
                fullWidth
                data-testid={testIds.controlPanel.startBtn}
                onClick={handleStart}
            >
                Start
            </Button>

            <Dialog open={showDialog} data-testid={testIds.controlPanel.startDialog}>
                <DialogTitle title="Start Profile" hideClose />
                <DialogContent>
                    {(mutation.isPending || applicationQuery.isFetching) && <Box display='flex' flexDirection='column' alignItems='center' gap={2}>
                        <InfoPanel isWarning title={warnings.DO_NOT_REFRESH} />
                        <Spinner />
                    </Box>}
                    {mutation.isError && (
                        <InfoPanel isWarning title='Unable to start profile' />
                    )}
                    {applicationQuery.isError && (
                        <InfoPanel isWarning title='Unable to fetch application. Refresh page.' />
                    )}
                </DialogContent>
                {mutation.isError || applicationQuery.isError ? <DialogActions>
                    <Button variant="outlined" onClick={handleCloseDialog}>Close</Button>
                </DialogActions> : null}
            </Dialog>
        </>
    );
};

StartForm.propTypes = {
    symbol: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    triggerStart: PropTypes.number
};

export default StartForm;